import { ReactNode } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { cssVar } from "../../utils/cssVar";
import { CssBaseline } from "@mui/material";
import { plex } from "../../utils/fonts";

type FormThemeProviderProp = { children: ReactNode };

export default function FormThemeProvider({ children }: FormThemeProviderProp) {
  const base = createTheme();
  const theme = createTheme({
    typography: {
      fontFamily: plex,
      fontWeightRegular: 500,
      fontWeightMedium: 500,
    },
    palette: {
      primary: { main: cssVar("--primary-color") },
      warning: { main: cssVar("--warning-color") },
      error: base.palette.error,
    },
    components: {
      MuiTextField: {
        defaultProps: {
          variant: "filled",
          fullWidth: true,
        },
      },
      MuiFilledInput: {
        defaultProps: {
          disableUnderline: true,
        },

        styleOverrides: {
          root: {
            backgroundColor: "#F7F7F7",
            borderRadius: "3px",
          },
          adornedEnd: {
            backgroundColor: "#F7F7F7",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontFamily: plex,
            fontWeight: 400,
            paddingTop: 25,
            paddingBottom: 10,
          },
        },
      },
      MuiCheckbox: {
        defaultProps: { size: "small", color: "primary" },
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: 6,
            "&.Mui-checked": { color: "var(--primary-color)" },
            "& .MuiSvgIcon-root": {
              fontSize: "14px",
            },
          },
        },
      },
      MuiRadio: {
        defaultProps: { size: "small", color: "primary" },
        styleOverrides: {
          root: {
            padding: 6,
            "&.Mui-checked": { color: "var(--primary-color)" },
            "& .MuiSvgIcon-root": {
              fontSize: "14px",
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          valueLabel: { fontFamily: plex, fontWeight: 500 },
          markLabel: { fontFamily: plex, fontWeight: 500 },
          mark: {
            width: 4,
            height: 4,
            borderRadius: "50%",
            backgroundColor: "white",
          },
          thumb: {
            backgroundImage: cssVar("--slider-linear-gradient"),
          },
          track: {
            border: "none",
            backgroundImage: cssVar("--slider-linear-gradient"),
          },
          rail: {
            marginLeft: "12px",
            marginRight: "12x",
            height: 6,
            backgroundImage: cssVar("--slider-linear-gradient"),
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          outlined: {
            borderRadius: 20,
          },
          contained: {
            color: "white",
            borderRadius: "var(--send-button-border-radius)",
            paddingY: "var(--send-button-vertical-padding)",
            paddingX: "var(--send-button-horizontal-padding)",
            textTransform: "none",
            ":hover": {
              backgroundColor: "var(--send-button-hover-bg-color)",
              filter: "brightness(0.95)",
            },
          },
        },
      },

      MuiFormControl: {
        styleOverrides: {
          root:
            // Tighter spacing
            { marginTop: 8, marginBottom: 8 },
        },
      },
      MuiFormLabel: {
        styleOverrides: { root: { fontFamily: plex, fontWeight: 400 } },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontFamily: plex,
            fontWeight: 500,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          // The font stack and weight already come from `typography` above; the
          // only thing that differs from the MUI default is the placeholder,
          // which the design wants at full opacity rather than 0.42.
          input: {
            "&::placeholder": { opacity: 1 },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontFamily: plex,
            fontWeight: 400,
            fontSize: "15px",
            lineHeight: 1.35,
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
